// SwitcherPopupHeader.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';
import useWorkspaceAccess from 'hooks/useWorkspaceAccess';

// Services
import {
    isWorkspaceBillingInGracePeriod,
    isWorkspaceBillingNavigationLocked,
    subscribeToWorkspaceBillingRecord,
} from 'services/billing/WorkspaceBillingService';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// CSS
import './SwitcherPopupHeader.css';

// Components
import Button from 'components/button/Button';
import LogoSmall from 'components/logo/small/LogoSmall';
import WorkspaceCover from 'components/workspacecover/WorkspaceCover';

function formatWorkspaceBillingNotice(record) {
    if (!record) return '';

    const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
        ? record.gracePeriodEndsAt
        : null;

    if (isWorkspaceBillingNavigationLocked(record)) {
        return 'Billing access is locked. Open workspace settings to update payment and restore workspace navigation.';
    }

    if (!isWorkspaceBillingInGracePeriod(record)) {
        return '';
    }

    if (!gracePeriodEndsAt) {
        return 'Billing issue detected. Update payment to avoid navigation lock.';
    }

    return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()}.`;
}

/**
 * SwitcherPopupHeader
 * 
 * Displays the header section of the switcher popup.
 *  
 * @returns {ReactNode} - SwitcherPopupHeader.
 */
const SwitcherPopupHeader = React.memo(({ onPlanClick, onSettingsClick, onInviteClick, activeMode = null }) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();
    const { can } = useWorkspaceAccess(activeWorkspace);
    const canReadWorkspaceBilling = can('workspace.update');

    // User Context
    const { user } = useUser();

    // ----- STATE ----- //

    const [billingRecord, setBillingRecord] = useState(null);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!canReadWorkspaceBilling) {
            setBillingRecord(null);
            return () => {};
        }

        return subscribeToWorkspaceBillingRecord(activeWorkspace?.key, setBillingRecord);
    }, [activeWorkspace?.key, canReadWorkspaceBilling]);

    // ----- MEMOS ----- //

    const billingNotice = useMemo(() => {
        return formatWorkspaceBillingNotice(billingRecord);
    }, [billingRecord]);

    const workspaceNavigationLocked = useMemo(() => {
        return isWorkspaceBillingNavigationLocked(billingRecord);
    }, [billingRecord]);

    const canInviteMembers = can('invite.create') && !workspaceNavigationLocked;
    const workspacePlanTitle = String(activeWorkspace?.planTitle || '').trim();

    // ----- HANDLERS ----- //

    /**
     * Handles click on the settings button.
     */
    const handleSettingsClick = () => {
        onSettingsClick && onSettingsClick();
    };

    /**
     * Handles click on the invite members button.
     */
    const handleInviteClick = () => {
        onInviteClick && onInviteClick();
    };

    /**
     * Handles click on the plan button.
     */
    const handlePlanClick = () => {
        onPlanClick && onPlanClick();
    };

    // ----- RENDER ----- //

    return (

        <div className="switcher-popup-header-container"
            style={{
                backgroundColor: theme.popup,
                borderBottomColor: theme.backgroundMuted,
            }}>

            {/* LOGO HEADER 
            <div className="sidebar-logo-header">

                <div className="sidebar-logo">
                    <LogoSmall
                        showSubtext={false}
                    />
                </div>

            </div>
            */}

            {/* TOP */}
            <div className="switcher-popup-header-top">

                {/* PHOTO 
                <WorkspaceCover
                    workspace={activeWorkspace}
                    borderWidth={0}
                    borderRadius={6}
                    size={36}
                    shape={PhotoShape.SQUARE}
                />
                */}

                {/* RIGHT */}
                <div className="switcher-popup-header-right">

                    {/* TITLE */}
                    <div className="switcher-popup-title-text"
                        style={{
                            color: theme.foreground,
                        }}>
                        {activeWorkspace?.title || ''}
                    </div>

                    {/* SUBTITLE */}
                    <div className="switcher-popup-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}>
                        {workspacePlanTitle}
                    </div>

                    {billingNotice && (
                        <div
                            className="switcher-popup-billing-alert"
                            style={{
                                backgroundColor: workspaceNavigationLocked
                                    ? (theme.warning || theme.backgroundHover)
                                    : theme.backgroundHover,
                                borderColor: workspaceNavigationLocked
                                    ? (theme.warning || theme.backgroundHover)
                                    : theme.backgroundHover,
                                color: theme.foreground,
                            }}
                        >
                            {billingNotice}
                        </div>
                    )}

                    {/* EMAIL */}
                    <div className="switcher-popup-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {user?.data_profile?.email || user?.email || ''}
                    </div>

                </div>

            </div>

            {/* BOTTOM */}
            <div className="switcher-popup-header-bottom">

                {billingNotice && (
                    <Button
                        iconKey="paid"
                        iconSize={16}
                        label="Open Plan"
                        height="40px"
                        width="auto"
                        borderRadius="9px"
                        fontSize="12px"
                        hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                        hoverForegroundColor={theme.buttonHoverLabelColor}
                        onClick={handlePlanClick}
                    />
                )}

                <Button
                    iconKey="settings"
                    iconSize={16}
                    label="Settings"
                    height="40px"
                    width="auto"
                    borderRadius="9px"
                    flex={1}
                    fontSize="12px"
                    hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                    hoverForegroundColor={theme.buttonHoverLabelColor}
                    onClick={handleSettingsClick}
                />

                <Button
                    iconKey="person_add"
                    iconSize={16}
                    label="Members"
                    enabled={canInviteMembers}
                    height="40px"
                    width="auto"
                    borderRadius="9px"
                    fontSize="12px"
                    flex={1}
                    hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                    hoverForegroundColor={theme.buttonHoverLabelColor}
                    onClick={handleInviteClick}
                />

            </div>

        </div>
    );

});

// Dev Tools
SwitcherPopupHeader.displayName = 'SwitcherPopupHeader';

export default SwitcherPopupHeader;

